import { useEffect, useRef, useState } from "react";
import { Check, Loader2, AlertTriangle, X } from "lucide-react";
import { CriteriaToggles, ScoreInput, isPbgs } from "@/components/testsessions/Pbgs";

const CRIT = ["Sequenza dei piedi", "Assetto di guardia", "Continuità e controllo", "Gesto specifico"];
const SHORT = ["S", "G", "C", "T"];

const fromResult = (res, planned) => Array.from({ length: planned }, (_, i) => {
  const a = (res?.attempt_details || []).find((x) => x.index === i + 1);
  return a ? { time: a.time ?? "", valid: a.valid, void_reason: a.void_reason || "", criteria: a.criteria || {}, criteria_status: a.criteria_status || {}, score: a.score ?? null }
    : { time: "", valid: true, void_reason: "", criteria: {}, criteria_status: {}, score: null };
});
const toAttempts = (cells, pbgs) => cells.map((c, i) => ({ ...c, index: i + 1 })).filter((c) => c.time !== "" || !c.valid)
  .map((c) => ({ index: c.index, time: c.time === "" ? null : parseFloat(String(c.time).replace(",", ".")), valid: c.valid, void_reason: c.valid ? null : c.void_reason,
    criteria: c.criteria, ...(pbgs ? { criteria_status: c.criteria_status, score: c.valid ? c.score : null } : {}) }));
export const rowState = (res, planned, absent) => {
  if (absent) return "absent";
  const det = res?.attempt_details || [];
  if (!det.length) return "missing";
  return det.filter((a) => a.valid).length >= planned ? "complete" : "partial";
};
const STATE = { missing: ["Mancante", "bg-zinc-100 text-zinc-600"], partial: ["Parziale", "bg-amber-100 text-amber-800"], complete: ["Completo", "bg-emerald-100 text-emerald-800"], absent: ["Assente", "bg-zinc-200 text-zinc-500"] };

function SaveIcon({ st }) {
  if (st === "saving") return <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />;
  if (st === "saved") return <Check className="w-4 h-4 text-emerald-600" />;
  if (st === "error" || st === "stale") return <AlertTriangle className="w-4 h-4 text-red-600" />;
  if (st === "dirty" || st === "incomplete") return <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />;
  return null;
}

export default function GridRow({ rowIdx, p, test, planned, result, absent, locked, reasons, onSave, onStatus }) {
  const [cells, setCells] = useState(() => fromResult(result, planned));
  const [st, setSt] = useState("idle");
  const timer = useRef(null);
  const rev = useRef(result?.rev ?? null);
  const busy = useRef(false);
  const pending = useRef(null);
  const editing = useRef(false);
  useEffect(() => { if (!editing.current && !busy.current && (st === "idle" || st === "saved")) { setCells(fromResult(result, planned)); rev.current = result?.rev ?? null; } }, [result, planned]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { onStatus(p.athlete_id, st); }, [st]); // eslint-disable-line react-hooks/exhaustive-deps
  const tech = !!test.fencing_technical;
  const pbgs = tech && isPbgs(test);

  const flush = async (next) => {
    if (busy.current) { pending.current = next; return; }
    if (next.some((c) => !c.valid && !c.void_reason)) return setSt("incomplete");
    const bad = next.some((c) => c.time !== "" && !(parseFloat(String(c.time).replace(",", ".")) > 0));
    if (bad) return setSt("error");
    busy.current = true; editing.current = false; setSt("saving");
    try {
      const r = await onSave(p.athlete_id, toAttempts(next, pbgs), rev.current); rev.current = r?.rev ?? null;
      busy.current = false;
      if (pending.current) { const q = pending.current; pending.current = null; return flush(q); }
      setSt(editing.current ? "dirty" : "saved");
    } catch (e) { busy.current = false; pending.current = null; setSt(e.response?.status === 409 ? "stale" : "error"); }
  };
  const update = (i, patch, immediate = false) => {
    const next = cells.map((c, j) => (j === i ? { ...c, ...patch } : c));
    editing.current = true;
    setCells(next); setSt("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(next), immediate ? 0 : 700);
  };
  const nav = (e, i) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const el = document.querySelector(`[data-nav="${test.id}-${rowIdx + 1}-${i}"]`) || document.querySelector(`[data-nav="${test.id}-0-${i + 1}"]`);
    el?.focus(); el?.select?.();
  };
  const [label, cls] = STATE[rowState(result, planned, absent)];

  return (
    <tr className="border-t border-zinc-100" data-testid={`grid-row-${p.athlete_id}`}>
      <td className={`sticky left-0 bg-white z-[1] px-3 py-2 min-w-[130px] sm:min-w-[180px] ${absent ? "text-zinc-400" : ""}`}>
        <div className="font-medium text-sm truncate">{p.last_name} {p.first_name}</div>
        <div className="flex items-center gap-2 mt-0.5">
          <span data-testid={`grid-state-${p.athlete_id}`} className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{label}</span>
          <span data-testid={`grid-save-${p.athlete_id}`} title={st}><SaveIcon st={st} /></span>
          {st === "stale" && <span className="text-[10px] text-red-700">modificato altrove: ricarica</span>}
          {st === "incomplete" && <span className="text-[10px] text-amber-700">motivo annullamento</span>}
        </div>
      </td>
      {cells.map((c, i) => (
        <td key={i} className={`px-2 py-2 align-top ${!c.valid ? "bg-red-50" : ""} ${absent ? "opacity-50" : ""}`}>
          <div className="flex items-center gap-1">
            <input data-testid={`cell-${p.athlete_id}-${i + 1}-time`} data-nav={`${test.id}-${rowIdx}-${i}`} inputMode="decimal" disabled={absent || locked}
              className="w-20 h-11 border border-zinc-300 rounded-md px-2 font-mono text-lg text-right disabled:bg-zinc-50" value={c.time}
              onChange={(e) => update(i, { time: e.target.value.replace(/[^0-9.,]/g, "") })} onBlur={() => st === "dirty" && (clearTimeout(timer.current), flush(cells))} onKeyDown={(e) => nav(e, i)} />
            <button type="button" data-testid={`cell-${p.athlete_id}-${i + 1}-void`} disabled={absent || locked} title={c.valid ? "Annulla prova" : "Ripristina come valida"}
              onClick={() => update(i, { valid: !c.valid, void_reason: "" }, true)} className={`w-9 h-11 rounded-md border flex items-center justify-center ${!c.valid ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-400"}`}><X className="w-4 h-4" /></button>
          </div>
          {pbgs && c.valid && (
            <div className="flex items-center gap-1 mt-1">
              <CriteriaToggles value={c.criteria_status} disabled={absent || locked} testid={`cell-${p.athlete_id}-${i + 1}-crit`} examples={test.technical_examples}
                onChange={(v) => update(i, { criteria_status: v }, true)} />
              <ScoreInput value={c.score} disabled={absent || locked} testid={`cell-${p.athlete_id}-${i + 1}-score`}
                navProps={{ onKeyDown: (e) => nav(e, i) }} onChange={(v) => update(i, { score: v })} />
            </div>
          )}
          {tech && !pbgs && c.valid && (
            <div className="flex items-center gap-1 mt-1">
              {CRIT.map((k, ci) => (
                <button type="button" key={k} title={k} data-testid={`cell-${p.athlete_id}-${i + 1}-c${ci + 1}`} disabled={absent || locked}
                  onClick={() => update(i, { criteria: { ...c.criteria, [k]: !c.criteria[k] } }, true)}
                  className={`w-9 h-9 rounded text-xs font-semibold border ${c.criteria[k] ? "bg-black text-white border-black" : "bg-white text-zinc-500"}`}>{SHORT[ci]}</button>
              ))}
              <span data-testid={`cell-${p.athlete_id}-${i + 1}-score`} className="ml-1 font-mono text-sm w-8">{c.time !== "" ? `${1 + CRIT.filter((k) => c.criteria[k]).length}/5` : "—"}</span>
            </div>
          )}
          {!c.valid && (
            <select data-testid={`cell-${p.athlete_id}-${i + 1}-reason`} disabled={locked} className="mt-1 w-full h-9 border border-red-300 rounded text-xs" value={c.void_reason} onChange={(e) => update(i, { void_reason: e.target.value }, true)}>
              <option value="">Motivo annullamento…</option>{reasons.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
        </td>
      ))}
    </tr>
  );
}
